<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import NotificationBell from '@/components/layout/NotificationBell.vue'
import { formatDateTime, timeAgo } from '@/utils/date'
import { useNotificationCenterStore } from '../stores/notification-center.store'
import { useNotificationCenterPolling } from '../composables/useNotificationCenterPolling'
import { notificationTarget } from '../utils/notification-center'
import type {
	AppNotification,
	NotificationKind,
	NotificationTarget,
} from '../types/notification-center.types'

const store = useNotificationCenterStore()
const router = useRouter()
useNotificationCenterPolling()

const open = ref(false)

// What was unread when the panel opened. Opening marks everything read (Vue 2 parity), but these
// keep their "new" mark while the panel stays open so the user can see what arrived.
const newIds = ref(new Set<string>())

function rememberUnread(): void {
	for (const item of store.items) if (item.unread) newIds.value.add(item.id)
}

async function onOpen(): Promise<void> {
	newIds.value = new Set()
	rememberUnread()
	void store.markAllRead()
	await store.refresh()
	// Anything that arrived with this refresh is new too.
	rememberUnread()
	void store.markAllRead()
}

watch(open, (isOpen) => {
	if (isOpen) void onOpen()
})

const targets = computed(
	() => new Map(store.items.map((item) => [item.id, notificationTarget(item.link, router)])),
)

function linkProps(item: AppNotification): Record<string, string> {
	const target: NotificationTarget | null | undefined = targets.value.get(item.id)
	if (!target) return {}
	if ('to' in target) return { to: target.to }
	return { href: target.href, target: '_blank', rel: 'noopener' }
}

function isExternal(item: AppNotification): boolean {
	const target = targets.value.get(item.id)
	return !!target && 'href' in target
}

function onItemClick(item: AppNotification): void {
	if (targets.value.get(item.id)) open.value = false
}

const KIND_ICON: Record<NotificationKind, string> = {
	'bulk-download': 'fa-solid fa-file-arrow-down',
	'list-refresh': 'fa-solid fa-arrows-rotate',
	'lead-enrichment': 'fa-solid fa-wand-magic-sparkles',
	'lead-scoring': 'fa-solid fa-chart-simple',
	'contact-refresh': 'fa-solid fa-address-card',
	general: 'fa-solid fa-circle-info',
}

function iconFor(item: AppNotification): string {
	return item.failed ? 'fa-solid fa-triangle-exclamation' : KIND_ICON[item.kind]
}

function colorFor(item: AppNotification): string {
	return item.failed ? 'warning' : 'primary'
}

const showSkeleton = computed(() => !store.loaded && !store.loadFailed)
</script>

<template>
	<v-menu
		v-model="open"
		location="bottom end"
		offset="8"
		:close-on-content-click="false"
	>
		<template #activator="{ props: menuProps }">
			<NotificationBell
				v-bind="menuProps"
				:unread-count="store.unreadCount"
				class="mx-1"
				data-test="notification-bell"
			/>
		</template>

		<v-card
			class="notification-center"
			rounded="lg"
			data-test="notification-center"
		>
			<div class="notification-center__header">
				<span class="text-subtitle-1 font-weight-bold">Notifications</span>
				<v-chip
					v-if="newIds.size > 0"
					size="x-small"
					color="primary"
					variant="tonal"
					class="ml-2"
					data-test="notification-new-count"
				>
					{{ newIds.size }} new
				</v-chip>
				<v-spacer />
				<v-btn
					icon
					size="small"
					variant="text"
					aria-label="Refresh notifications"
					:loading="store.loading && store.loaded"
					@click="store.refresh()"
				>
					<i class="fa-solid fa-arrows-rotate notification-center__refresh-icon" />
				</v-btn>
			</div>
			<v-divider />

			<div
				v-if="showSkeleton"
				class="pa-2"
			>
				<v-skeleton-loader
					v-for="n in 3"
					:key="n"
					type="list-item-avatar-two-line"
				/>
			</div>

			<div
				v-else-if="!store.loaded"
				class="notification-center__state"
				data-test="notification-error"
			>
				<i class="fa-solid fa-plug-circle-exclamation notification-center__state-icon" />
				<div class="text-subtitle-2">We couldn't load your notifications</div>
				<v-btn
					size="small"
					variant="tonal"
					color="primary"
					class="text-none mt-3"
					prepend-icon="fa-solid fa-rotate-right"
					@click="store.refresh()"
				>
					Try again
				</v-btn>
			</div>

			<div
				v-else-if="store.items.length === 0"
				class="notification-center__state"
				data-test="notification-empty"
			>
				<i class="fa-regular fa-bell-slash notification-center__state-icon" />
				<div class="text-subtitle-2">You're all caught up</div>
				<div class="text-body-2 text-medium-emphasis mt-1">
					We'll let you know here when your exports, enrichments and list refreshes finish.
				</div>
			</div>

			<v-list
				v-else
				class="notification-center__list py-1"
				density="comfortable"
			>
				<v-list-item
					v-for="item in store.items"
					:key="item.id"
					v-bind="linkProps(item)"
					class="notification-center__item"
					:class="{ 'notification-center__item--new': newIds.has(item.id) }"
					data-test="notification-item"
					@click="onItemClick(item)"
				>
					<template #prepend>
						<v-avatar
							size="36"
							rounded="lg"
							variant="tonal"
							:color="colorFor(item)"
						>
							<i
								:class="iconFor(item)"
								class="notification-center__kind-icon"
								:data-test="`notification-icon-${item.failed ? 'failed' : item.kind}`"
							/>
						</v-avatar>
					</template>

					<!-- Plain text: Vue 2 renders this with v-html, and messages carry list names. -->
					<div class="notification-center__message">{{ item.message }}</div>
					<div class="notification-center__meta">
						<time
							:datetime="item.createdAt"
							:title="formatDateTime(item.createdAt)"
						>
							{{ timeAgo(item.createdAt) }}
						</time>
						<span
							v-if="targets.get(item.id)"
							class="notification-center__open"
						>
							{{ isExternal(item) ? 'Open' : 'View' }}
							<i
								:class="isExternal(item) ? 'fa-solid fa-arrow-up-right-from-square' : 'fa-solid fa-arrow-right'"
								class="ml-1"
							/>
						</span>
					</div>

					<template #append>
						<span
							v-if="newIds.has(item.id)"
							class="notification-center__dot"
							aria-label="New"
						/>
					</template>
				</v-list-item>
			</v-list>
		</v-card>
	</v-menu>
</template>

<style scoped lang="scss">
.notification-center {
	width: 380px;
	max-width: calc(100vw - 32px);
}

.notification-center__header {
	display: flex;
	align-items: center;
	padding: 10px 8px 10px 16px;
}

.notification-center__refresh-icon {
	font-size: 13px;
}

.notification-center__list {
	max-height: 440px;
	overflow-y: auto;
}

.notification-center__item {
	align-items: flex-start;
	padding-top: 10px;
	padding-bottom: 10px;
	transition: background-color 200ms $ease-out-expo;

	&--new {
		background: rgba(var(--v-theme-primary), 0.06);
	}

	// Icon and dot sit by the first line, not the middle of a multi-line message.
	:deep(.v-list-item__prepend),
	:deep(.v-list-item__append) {
		align-self: flex-start;
	}

	:deep(.v-list-item__spacer) {
		width: 12px;
	}
}

.notification-center__kind-icon {
	font-size: 15px;
}

.notification-center__message {
	font-size: 0.875rem;
	line-height: 1.35;
	white-space: normal;
	word-break: break-word;
}

.notification-center__meta {
	display: flex;
	align-items: center;
	gap: 12px;
	margin-top: 4px;
	font-size: 0.75rem;
	color: rgba(var(--v-theme-on-surface), 0.6);
}

.notification-center__open {
	font-weight: 600;
	color: rgb(var(--v-theme-primary));
}

.notification-center__dot {
	display: inline-block;
	width: 8px;
	height: 8px;
	margin-top: 6px;
	border-radius: 50%;
	background: rgb(var(--v-theme-primary));
}

.notification-center__state {
	display: flex;
	flex-direction: column;
	align-items: center;
	padding: 32px 24px;
	text-align: center;
}

.notification-center__state-icon {
	font-size: 28px;
	margin-bottom: 12px;
	color: rgba(var(--v-theme-on-surface), 0.35);
}
</style>
